import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Sun, Moon, Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { useTheme } from "../theme/ThemeContext";
import { useI18n } from "../i18n/LanguageContext";

function LangSwitch() {
  const { lang, setLang, languages } = useI18n();
  return (
    <div className="flex items-center gap-1 text-xs font-medium" data-testid="lang-switch">
      {languages.map((l, i) => (
        <div key={l.code} className="flex items-center">
          <button
            type="button"
            onClick={() => setLang(l.code)}
            data-testid={`lang-${l.code}`}
            className={`px-1.5 py-0.5 tracking-wider transition-colors ${
              lang === l.code ? "text-accent" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {l.label}
          </button>
          {i < languages.length - 1 && <span className="text-muted-foreground/40">·</span>}
        </div>
      ))}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Toggle theme"
      data-testid="theme-toggle"
      className="h-9 w-9 grid place-items-center rounded-md border border-border text-foreground hover:border-accent hover:text-accent transition-colors"
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function Header() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/diensten", label: t.nav.services },
    { to: "/expat-housing", label: t.nav.expat },
    { to: "/bedrijfspanden", label: t.nav.business },
    { to: "/tarieven", label: t.nav.pricing },
    { to: "/dossiers", label: t.nav.cases },
    { to: "/over-ons", label: t.nav.about },
    { to: "/faq", label: t.nav.faq },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-background/85 border-b border-border"
          : "bg-background/40 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="container-wide flex items-center justify-between py-3.5">
        <Link to="/" aria-label="Tip Top Property home" data-testid="logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-tight transition-colors link-underline ${
                  isActive ? "text-accent" : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-3">
            <LangSwitch />
            <ThemeToggle />
            <Link
              to="/contact?intent=intake"
              data-testid="header-cta"
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              {t.nav.bookIntake}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <button
            type="button"
            className="lg:hidden h-9 w-9 grid place-items-center rounded-md border border-border"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="menu-toggle"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-nav">
          <div className="container-wide py-4 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mnav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-2 py-2.5 rounded-md text-sm ${
                    isActive ? "bg-secondary text-foreground" : "text-foreground/80 hover:bg-secondary/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-border">
              <LangSwitch />
              <ThemeToggle />
            </div>
            <Link
              to="/contact?intent=intake"
              data-testid="mobile-header-cta"
              className="mt-3 inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium px-4 py-3 rounded-md"
            >
              {t.nav.bookIntake}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
